/**
 * TR909Panel - Interface complète de la TR-909
 * Design avec sections groupées par instrument
 */

import { themeManager } from '../themes/ThemeManager.js';
import { PresetSelector } from '../components/PresetSelector.js';
import { TR909_PRESETS } from '../../state/InstrumentPresets.js';

export class TR909Panel {
    constructor(tr909, store, sequencer) {
        this.tr909 = tr909;
        this.store = store;
        this.sequencer = sequencer;
        this.panelId = 'tr909';

        // Références aux composants UI
        this.components = {
            knobs: {},
            stepButtons: {}
        };
    }

    isCollapsed() {
        return this.store?.isPanelCollapsed(this.panelId) || false;
    }

    render(container) {
        this.container = container;
        const isCollapsed = this.isCollapsed();
        const isPowered = this.store.getPowerState('tr909');

        this.tr909.setPower(isPowered);

        container.innerHTML = `
            <div class="machine tr909 ${!isPowered ? 'powered-off' : ''} ${isCollapsed ? 'collapsed' : ''}" id="tr909_machine">
                <div class="machine-title tr909-title">
                    <button class="collapse-btn" id="tr909_collapse" title="Réduire/Agrandir">${isCollapsed ? '▶' : '▼'}</button>
                    TR-909 <span class="brand">Rhythm Composer</span>
                    <div id="tr909_presets"></div>
                    <button class="power-btn ${isPowered ? 'on' : ''}" id="tr909_power" title="Power ON/OFF">⏻</button>
                </div>

                <div class="machine-content ${isCollapsed ? 'hidden' : ''}" id="tr909_content">
                <div class="tr909-controls">
                    <!-- KICK Section -->
                    <div class="tr909-section">
                        <div class="section-title">KICK</div>
                        <div class="controls">
                            <div class="knob-group" id="tr909_kick_level"></div>
                            <div class="knob-group" id="tr909_kick_attack"></div>
                            <div class="knob-group" id="tr909_kick_tune"></div>
                            <div class="knob-group" id="tr909_kick_decay"></div>
                        </div>
                    </div>

                    <!-- SNARE Section -->
                    <div class="tr909-section">
                        <div class="section-title">SNARE</div>
                        <div class="controls">
                            <div class="knob-group" id="tr909_snare_level"></div>
                            <div class="knob-group" id="tr909_snare_tone"></div>
                            <div class="knob-group" id="tr909_snare_tune"></div>
                            <div class="knob-group" id="tr909_snare_snappy"></div>
                        </div>
                    </div>

                    <!-- CLAP Section -->
                    <div class="tr909-section">
                        <div class="section-title">CLAP</div>
                        <div class="controls">
                            <div class="knob-group" id="tr909_clap_level"></div>
                            <div class="knob-group" id="tr909_clap_decay"></div>
                        </div>
                    </div>

                    <!-- RIMSHOT Section -->
                    <div class="tr909-section">
                        <div class="section-title">RIMSHOT</div>
                        <div class="controls">
                            <div class="knob-group" id="tr909_rim_level"></div>
                            <div class="knob-group" id="tr909_rim_tune"></div>
                        </div>
                    </div>

                    <!-- HI-HATS Section -->
                    <div class="tr909-section">
                        <div class="section-title">HI-HATS</div>
                        <div class="controls">
                            <div class="knob-group" id="tr909_hh_level"></div>
                            <div class="knob-group" id="tr909_hh_decay"></div>
                            <div class="knob-group" id="tr909_ohh_level"></div>
                            <div class="knob-group" id="tr909_ohh_decay"></div>
                        </div>
                    </div>

                    <!-- CYMBALS Section -->
                    <div class="tr909-section cymbals-section">
                        <div class="section-title">CYMBALS</div>
                        <div class="controls">
                            <div class="knob-group" id="tr909_ride_level"></div>
                            <div class="knob-group" id="tr909_ride_tune"></div>
                            <div class="knob-group" id="tr909_ride_decay"></div>
                            <div class="knob-group" id="tr909_crash_level"></div>
                            <div class="knob-group" id="tr909_crash_tune"></div>
                            <div class="knob-group" id="tr909_crash_decay"></div>
                        </div>
                    </div>
                </div>

                <div class="sequencer machine-sequencer">
                    <div class="step-markers">
                        ${Array.from({length: 16}, (_, i) => `<span class="step-marker">${i + 1}</span>`).join('')}
                    </div>
                    <div class="seq-row">
                        <span class="seq-label">KICK</span>
                        <div class="steps" id="kick909Steps"></div>
                    </div>
                    <div class="seq-row">
                        <span class="seq-label">SNARE</span>
                        <div class="steps" id="snare909Steps"></div>
                    </div>
                    <div class="seq-row">
                        <span class="seq-label">CLAP</span>
                        <div class="steps" id="clap909Steps"></div>
                    </div>
                    <div class="seq-row">
                        <span class="seq-label">RIMSHOT</span>
                        <div class="steps" id="rimshot909Steps"></div>
                    </div>
                    <div class="seq-row">
                        <span class="seq-label">HI-HAT</span>
                        <div class="steps" id="hh909Steps"></div>
                    </div>
                    <div class="seq-row">
                        <span class="seq-label">OPEN HH</span>
                        <div class="steps" id="openhh909Steps"></div>
                    </div>
                    <div class="seq-row">
                        <span class="seq-label">RIDE</span>
                        <div class="steps" id="ride909Steps"></div>
                    </div>
                    <div class="seq-row">
                        <span class="seq-label">CRASH</span>
                        <div class="steps" id="crash909Steps"></div>
                    </div>
                </div>
                </div><!-- /machine-content -->
            </div>
        `;

        this.setupCollapseButton();
        this.setupPowerButton();

        if (!isCollapsed) {
            this.createKnobs();
            this.createDrumSteps();
            this.setupPresetSelector();
        }
    }

    setupCollapseButton() {
        const collapseBtn = document.getElementById('tr909_collapse');
        if (!collapseBtn) return;

        collapseBtn.addEventListener('click', () => {
            this.store.togglePanelCollapsed(this.panelId);
            this.destroy();
            this.render(this.container);
        });
    }

    setupPowerButton() {
        const powerBtn = document.getElementById('tr909_power');
        const machine = document.getElementById('tr909_machine');

        powerBtn.addEventListener('click', () => {
            const isOn = this.tr909.getPower();
            const newState = !isOn;
            this.tr909.setPower(newState);
            this.store.setPowerState('tr909', newState);

            powerBtn.classList.toggle('on', newState);
            machine.classList.toggle('powered-off', !newState);
        });
    }

    setupPresetSelector() {
        const container = document.getElementById('tr909_presets');
        if (!container) return;

        this.presetSelector = new PresetSelector({
            presets: TR909_PRESETS,
            currentPreset: 'Classic 909',
            onChange: (name, data) => this._loadPreset(data)
        });

        container.appendChild(this.presetSelector.element);
    }

    _loadPreset(presetData) {
        if (!presetData) return;

        // Apply preset parameters
        Object.keys(presetData).forEach(key => {
            const setter = `set${key.charAt(0).toUpperCase()}${key.slice(1)}`;
            if (typeof this.tr909[setter] === 'function') {
                this.tr909[setter](presetData[key]);
            }
        });

        // Update knobs
        if (presetData.kickAttack !== undefined) this.components.knobs.kickAttack?.setValue(presetData.kickAttack);
        if (presetData.kickDecay !== undefined) this.components.knobs.kickDecay?.setValue(presetData.kickDecay);
        if (presetData.kickTune !== undefined) this.components.knobs.kickTune?.setValue(presetData.kickTune);
        if (presetData.snareSnappy !== undefined) this.components.knobs.snareSnappy?.setValue(presetData.snareSnappy);
        if (presetData.snareTune !== undefined) this.components.knobs.snareTune?.setValue(presetData.snareTune);
        if (presetData.hhDecay !== undefined) this.components.knobs.hhDecay?.setValue(presetData.hhDecay);

        this._saveParams();
    }

    createKnobs() {
        const params = this.tr909.getParams();

        // === KICK ===
        this.components.knobs.kickLevel = themeManager.createKnob(
            document.getElementById('tr909_kick_level'), {
                min: 0, max: 1, value: params.kickLevel,
                label: 'Level', step: 0.01
            }
        ).onChange(v => { this.tr909.setKickLevel(v); this._saveParams(); });

        this.components.knobs.kickAttack = themeManager.createKnob(
            document.getElementById('tr909_kick_attack'), {
                min: 0, max: 1, value: params.kickAttack,
                label: 'Attack', step: 0.01
            }
        ).onChange(v => { this.tr909.setKickAttack(v); this._saveParams(); });

        this.components.knobs.kickTune = themeManager.createKnob(
            document.getElementById('tr909_kick_tune'), {
                min: 40, max: 100, value: params.kickTune,
                label: 'Tune', unit: 'Hz'
            }
        ).onChange(v => { this.tr909.setKickTune(v); this._saveParams(); });

        this.components.knobs.kickDecay = themeManager.createKnob(
            document.getElementById('tr909_kick_decay'), {
                min: 0.2, max: 0.8, value: params.kickDecay,
                label: 'Decay', unit: 's', step: 0.01, decimals: 2
            }
        ).onChange(v => { this.tr909.setKickDecay(v); this._saveParams(); });

        // === SNARE ===
        this.components.knobs.snareLevel = themeManager.createKnob(
            document.getElementById('tr909_snare_level'), {
                min: 0, max: 1, value: params.snareLevel,
                label: 'Level', step: 0.01
            }
        ).onChange(v => { this.tr909.setSnareLevel(v); this._saveParams(); });

        this.components.knobs.snareTone = themeManager.createKnob(
            document.getElementById('tr909_snare_tone'), {
                min: 0, max: 1, value: params.snareTone,
                label: 'Tone', step: 0.01
            }
        ).onChange(v => { this.tr909.setSnareTone(v); this._saveParams(); });

        this.components.knobs.snareTune = themeManager.createKnob(
            document.getElementById('tr909_snare_tune'), {
                min: 0, max: 1, value: params.snareTune,
                label: 'Tune', step: 0.01
            }
        ).onChange(v => { this.tr909.setSnareTune(v); this._saveParams(); });

        this.components.knobs.snareSnappy = themeManager.createKnob(
            document.getElementById('tr909_snare_snappy'), {
                min: 0, max: 1, value: params.snareSnappy,
                label: 'Snappy', step: 0.01
            }
        ).onChange(v => { this.tr909.setSnareSnappy(v); this._saveParams(); });

        // === CLAP ===
        this.components.knobs.clapLevel = themeManager.createKnob(
            document.getElementById('tr909_clap_level'), {
                min: 0, max: 1, value: params.clapLevel,
                label: 'Level', step: 0.01
            }
        ).onChange(v => { this.tr909.setClapLevel(v); this._saveParams(); });

        this.components.knobs.clapDecay = themeManager.createKnob(
            document.getElementById('tr909_clap_decay'), {
                min: 0.05, max: 0.3, value: params.clapDecay,
                label: 'Decay', unit: 's', step: 0.01, decimals: 2
            }
        ).onChange(v => { this.tr909.setClapDecay(v); this._saveParams(); });

        // === RIMSHOT ===
        this.components.knobs.rimLevel = themeManager.createKnob(
            document.getElementById('tr909_rim_level'), {
                min: 0, max: 1, value: params.rimshotLevel,
                label: 'Level', step: 0.01
            }
        ).onChange(v => { this.tr909.setRimshotLevel(v); this._saveParams(); });

        this.components.knobs.rimTune = themeManager.createKnob(
            document.getElementById('tr909_rim_tune'), {
                min: 0, max: 1, value: params.rimshotTune,
                label: 'Tune', step: 0.01
            }
        ).onChange(v => { this.tr909.setRimshotTune(v); this._saveParams(); });

        // === HI-HATS ===
        this.components.knobs.hhLevel = themeManager.createKnob(
            document.getElementById('tr909_hh_level'), {
                min: 0, max: 1, value: params.hhLevel,
                label: 'CH Lvl', step: 0.01
            }
        ).onChange(v => { this.tr909.setHHLevel(v); this._saveParams(); });

        this.components.knobs.hhDecay = themeManager.createKnob(
            document.getElementById('tr909_hh_decay'), {
                min: 0.02, max: 0.2, value: params.hhDecay,
                label: 'CH Dec', unit: 's', step: 0.01, decimals: 2
            }
        ).onChange(v => { this.tr909.setHihatDecay(v); this._saveParams(); });

        this.components.knobs.ohhLevel = themeManager.createKnob(
            document.getElementById('tr909_ohh_level'), {
                min: 0, max: 1, value: params.openHHLevel,
                label: 'OH Lvl', step: 0.01
            }
        ).onChange(v => { this.tr909.setOpenHHLevel(v); this._saveParams(); });

        this.components.knobs.ohhDecay = themeManager.createKnob(
            document.getElementById('tr909_ohh_decay'), {
                min: 0.1, max: 0.8, value: params.openHHDecay,
                label: 'OH Dec', unit: 's', step: 0.01, decimals: 2
            }
        ).onChange(v => { this.tr909.setOpenHHDecay(v); this._saveParams(); });

        // === CYMBALS (RIDE) ===
        this.components.knobs.rideLevel = themeManager.createKnob(
            document.getElementById('tr909_ride_level'), {
                min: 0, max: 1, value: params.rideLevel,
                label: 'Ride Lvl', step: 0.01
            }
        ).onChange(v => { this.tr909.setRideLevel(v); this._saveParams(); });

        this.components.knobs.rideTune = themeManager.createKnob(
            document.getElementById('tr909_ride_tune'), {
                min: 0, max: 1, value: params.rideTune,
                label: 'Ride Tune', step: 0.01
            }
        ).onChange(v => { this.tr909.setRideTune(v); this._saveParams(); });

        this.components.knobs.rideDecay = themeManager.createKnob(
            document.getElementById('tr909_ride_decay'), {
                min: 0.5, max: 3, value: params.rideDecay,
                label: 'Ride Dec', unit: 's', step: 0.1, decimals: 1
            }
        ).onChange(v => { this.tr909.setRideDecay(v); this._saveParams(); });

        // === CYMBALS (CRASH) ===
        this.components.knobs.crashLevel = themeManager.createKnob(
            document.getElementById('tr909_crash_level'), {
                min: 0, max: 1, value: params.crashLevel,
                label: 'Crash Lvl', step: 0.01
            }
        ).onChange(v => { this.tr909.setCrashLevel(v); this._saveParams(); });

        this.components.knobs.crashTune = themeManager.createKnob(
            document.getElementById('tr909_crash_tune'), {
                min: 0, max: 1, value: params.crashTune,
                label: 'Crash Tune', step: 0.01
            }
        ).onChange(v => { this.tr909.setCrashTune(v); this._saveParams(); });

        this.components.knobs.crashDecay = themeManager.createKnob(
            document.getElementById('tr909_crash_decay'), {
                min: 1, max: 5, value: params.crashDecay,
                label: 'Crash Dec', unit: 's', step: 0.1, decimals: 1
            }
        ).onChange(v => { this.tr909.setCrashDecay(v); this._saveParams(); });
    }

    createDrumSteps() {
        const drums = [
            { id: 'kick909Steps', pattern: 'kick909' },
            { id: 'snare909Steps', pattern: 'snare909' },
            { id: 'clap909Steps', pattern: 'clap909' },
            { id: 'rimshot909Steps', pattern: 'rimshot909' },
            { id: 'hh909Steps', pattern: 'hh909' },
            { id: 'openhh909Steps', pattern: 'openhh909' },
            { id: 'ride909Steps', pattern: 'ride909' },
            { id: 'crash909Steps', pattern: 'crash909' }
        ];

        drums.forEach(({ id, pattern }) => {
            const container = document.getElementById(id);
            this.components.stepButtons[pattern] = [];

            for (let i = 0; i < 16; i++) {
                const stepBtn = themeManager.createStepButton(container, {
                    index: i,
                    type: 'gate',
                    active: this.store.patterns[pattern][i]
                }).onChange(isActive => {
                    this.store.setStep(pattern, i, isActive);
                });
                this.components.stepButtons[pattern].push(stepBtn);
            }
        });
    }

    _saveParams() {
        this.store?.setSynthParams('tr909', this.tr909.getParams());
    }

    updateFromParams() {
        const params = this.tr909.getParams();
        const { knobs } = this.components;

        // Update all knobs
        if (knobs.kickLevel) knobs.kickLevel.setValue(params.kickLevel);
        if (knobs.kickAttack) knobs.kickAttack.setValue(params.kickAttack);
        if (knobs.kickTune) knobs.kickTune.setValue(params.kickTune);
        if (knobs.kickDecay) knobs.kickDecay.setValue(params.kickDecay);
        if (knobs.snareLevel) knobs.snareLevel.setValue(params.snareLevel);
        if (knobs.snareTone) knobs.snareTone.setValue(params.snareTone);
        if (knobs.snareTune) knobs.snareTune.setValue(params.snareTune);
        if (knobs.snareSnappy) knobs.snareSnappy.setValue(params.snareSnappy);
        if (knobs.clapLevel) knobs.clapLevel.setValue(params.clapLevel);
        if (knobs.clapDecay) knobs.clapDecay.setValue(params.clapDecay);
        if (knobs.rimLevel) knobs.rimLevel.setValue(params.rimshotLevel);
        if (knobs.rimTune) knobs.rimTune.setValue(params.rimshotTune);
        if (knobs.hhLevel) knobs.hhLevel.setValue(params.hhLevel);
        if (knobs.hhDecay) knobs.hhDecay.setValue(params.hhDecay);
        if (knobs.ohhLevel) knobs.ohhLevel.setValue(params.openHHLevel);
        if (knobs.ohhDecay) knobs.ohhDecay.setValue(params.openHHDecay);
        if (knobs.rideLevel) knobs.rideLevel.setValue(params.rideLevel);
        if (knobs.rideTune) knobs.rideTune.setValue(params.rideTune);
        if (knobs.rideDecay) knobs.rideDecay.setValue(params.rideDecay);
        if (knobs.crashLevel) knobs.crashLevel.setValue(params.crashLevel);
        if (knobs.crashTune) knobs.crashTune.setValue(params.crashTune);
        if (knobs.crashDecay) knobs.crashDecay.setValue(params.crashDecay);
    }

    destroy() {
        Object.values(this.components.knobs).forEach(knob => knob?.destroy());
        Object.values(this.components.stepButtons).forEach(buttons => {
            buttons.forEach(btn => btn?.destroy());
        });
    }
}
